/* eslint-disable react/jsx-props-no-spreading */
import { Box, Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField as MuiTextField, Typography } from '@mui/material';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';

import {
  API_CUSTOM_SEGMENTS_CREATE_URL,
  API_CUSTOM_SEGMENTS_EDIT_URL,
  API_GEOLINK_AUDIENCE_QUEUE_TRANSFER_URL,
  API_S3_UPLOAD_INFO_URL,
} from '../../constants/apiUrls';
import { CUSTOM_SEGMENTS_EDIT_URL } from '../../constants/routeUrls';
import { useAuth } from '../../hooks/useAuth';
import { useConfig } from '../../hooks/useConfig';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { useConstants } from '../../hooks/useConstants';
import { useSnackBar } from '../../hooks/useSnackBar';
import { camelCaseKeys, snakeCaseKeys } from '../../util/object';
import { getRequestJSON, postRequestJSON, putRequestJSON } from '../../util/services';
import Checkbox from '../forms/Checkbox';
import FormEndOptions from '../forms/FormEndOptions';
import S3Upload from '../forms/S3Upload';
import TextField from '../forms/TextField';
import AssociatedAdGroups from './AssociatedAdGroups';
import TaskErrorRow from './TaskErrorRow';

interface FormTaskProps {
  apiResponseCode: string;
  apiData: any[];
}

interface CustomSegmentsFormInputs {
  id: number;
  name: string;
  paths: any[];
  tasks: FormTaskProps;
  s3FilePath: string;
  unusedFieldName: string;
  associatedAdgroups: number[];
  retireExempt: boolean;
  // Set by the ad-group modal footer ("Save and Add to Ad Group") and read by
  // its onCreateSuccess to decide whether to attach the new segment.
  saveAndAdd?: boolean;
}

interface CustomSegmentsFormProps {
  id?: number;
  onCreateSuccess?: any;
  renderFooter?: any;
}

export const defaultValues: CustomSegmentsFormInputs = {
  id: undefined,
  name: '',
  paths: [],
  tasks: {
    apiResponseCode: undefined,
    apiData: [],
  },
  s3FilePath: undefined,
  unusedFieldName: undefined,
  associatedAdgroups: [],
  retireExempt: false,
};

const normalizePayload = (form) => snakeCaseKeys({ ...form, s3FilePath: undefined, unusedFieldName: undefined, associatedAdgroups: undefined, id: undefined });
const normalizeForm = (data) =>
  camelCaseKeys({
    ...data,
    s3FilePath: '',
    unusedFieldName: '',
  });

function CustomSegmentsForm({
  id: customSegmentId,
  onCreateSuccess: onCreateSuccessProp,
  renderFooter: renderFooterProp,
}: CustomSegmentsFormProps) {
  const { apiHost, geolinkAudienceCreationServiceApiHost } = useConfig();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queryText, setQueryText] = useState('');
  const [isQuerySubmitting, setIsQuerySubmitting] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);

  const isEdit = Boolean(customSegmentId);

  const { showSnackBar } = useSnackBar();

  const { constants } = useConstants();

  const STAGE = get(constants, 'RAILS.STAGE');
  const VENTURA_UPLOADS_BUCKET = get(constants, 'BUCKETS.VENTURA_UPLOADS_BUCKET');

  const form = useForm<CustomSegmentsFormInputs>({
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    getValues,
    control,
    watch,
    formState: { isDirty },
  } = form;

  useConfirmNavChange(() => isDirty);

  const {
    fields: pathsFieldArray,
    append,
    replace,
  } = useFieldArray({
    name: 'paths',
    control,
  });

  const tasks = watch('tasks');

  const onS3UploadSuccess = (link, path) => {
    append(`s3://${VENTURA_UPLOADS_BUCKET}/${path}`);
  };

  const onAddS3FilePath = () => {
    const s3FilePath = getValues('s3FilePath');
    if (s3FilePath) {
      append(s3FilePath);
      setValue('s3FilePath', '');
    }
  };

  const onClearFiles = () => {
    replace([]);
  };

  const onSubmit = async (fields) => {
    const normalizedPayload = normalizePayload(fields);
    const payload = {
      ...normalizedPayload,
      timeStamp: new Date().getTime(),
    };

    let response;
    if (isEdit) {
      response = await putRequestJSON(
        apiHost,
        generatePath(API_CUSTOM_SEGMENTS_EDIT_URL, { id: customSegmentId.toString() }),
        payload
      );
    } else {
      response = await postRequestJSON(apiHost, API_CUSTOM_SEGMENTS_CREATE_URL, payload);
    }

    const { errors } = response;

    if (errors) {
      let messages;
      // `errors` can be an array of strings, or an array.
      if (Array.isArray(errors)) {
        messages = errors;
      } else {
        messages = [errors];
      }
      messages.forEach((message) => {
        showSnackBar(message, 'error', { event: 'custom_segment_save_error' });
      });
      return;
    }

    const newModelId = get(response, 'model.id');
    showSnackBar('Successfully saved', 'success', {
      event: isEdit ? 'custom_segment_updated' : 'custom_segment_created',
      segment_id: isEdit ? customSegmentId : newModelId,
    });
    reset(normalizeForm(response?.model));

    const onCreateSuccess =
      onCreateSuccessProp ||
      (() =>
        setTimeout(
          () =>
            navigate(
              generatePath(CUSTOM_SEGMENTS_EDIT_URL, {
                id: newModelId,
              })
            ),
          1000
        ));

    if (!isEdit && newModelId) {
      if (onCreateSuccess) {
        onCreateSuccess({
          ...fields,
          ...response?.model,
        });
      }
    }
  };

  useEffect(() => {
    if (isEdit) {
      getRequestJSON(apiHost, generatePath(API_CUSTOM_SEGMENTS_EDIT_URL, { id: customSegmentId.toString() })).then(
        (response) => {
          if (response.error) {
            showSnackBar(response.error, 'error', {
              event: 'custom_segment_load_error',
              segment_id: customSegmentId,
            });
          }

          if (response.id) {
            const formValues = normalizeForm(response);
            reset(formValues);
          }
        }
      );
    }
  }, [isEdit, apiHost, customSegmentId, reset, showSnackBar]);

  const generateKey = () =>
    `${STAGE}/custom_segments/uploads/${customSegmentId ? `custom_segment=${customSegmentId}` : 'tmp'}/${Date.now()}`;

  const renderFooter =
    renderFooterProp ||
    ((onSaveParameter, isDirtyParameter) => (
      <>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
        <Button onClick={onSaveParameter} disabled={!isDirtyParameter}>
          Save Custom Segment
        </Button>
      </>
    ));

  const handleQuerySubmit = async () => {
    if (!queryText.trim()) {
      setQueryError('Query is required');
      return;
    }

    setIsQuerySubmitting(true);
    setQueryError(null);

    try {
      const token = user?.jwt_token || user?.sso_token;

      if (!token) {
        setQueryError('Authentication token not available. Please log in again.');
        showSnackBar('Authentication token not available. Please log in again.', 'error', {
          event: 'custom_segment_query_error',
          segment_id: customSegmentId,
        });
        setIsQuerySubmitting(false);
        return;
      }

      const apiUrl = `${geolinkAudienceCreationServiceApiHost}${API_GEOLINK_AUDIENCE_QUEUE_TRANSFER_URL}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ query: queryText, ...(getValues('name')?.trim() ? { transferId: getValues('name').trim() } : {}) }),
      }).then(res => {
        if (res.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        }
        return res.json();
      });

      if (response.errors || response.error) {
        let messages;
        if (Array.isArray(response.errors)) {
          messages = response.errors.join(', ');
        } else if (response.error) {
          messages = response.error;
        } else {
          messages = response.errors;
        }
        setQueryError(messages);
        showSnackBar(messages, 'error', { event: 'custom_segment_query_error' });
      } else {
        // event fired on the subsequent save snackbar below
        if (response.s3Bucket && response.s3FileName) {
          const finalS3FileName = response.s3FileName.replace('/intermediate_uploads/', '/uploads/');
          const fullS3Path = `s3://${response.s3Bucket}/${finalS3FileName}`;
          append(fullS3Path);
          setValue('s3FilePath', '', { shouldDirty: true });
          const fileNameOnly = finalS3FileName.split('/').pop()?.replace(/\.[^/.]+$/, '') || response.transferId;
          if (fileNameOnly) {
            setValue('name', fileNameOnly, { shouldDirty: true });
          }
        }
        showSnackBar('Query queued for transfer successfully. Saving segment...', 'success', {
          event: 'custom_segment_query_submitted',
          segment_id: customSegmentId,
        });
        setQueryText('');
        // Hosted in the ad-group modal (onCreateSuccessProp set), a query submit
        // must attach the segment to the ad group without a footer-button click,
        // and onCreateSuccess only attaches when saveAndAdd is set.
        if (onCreateSuccessProp) {
          setValue('saveAndAdd', true);
        }
        await handleSubmit(onSubmit)();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setQueryError(errorMessage);
      showSnackBar(errorMessage, 'error', { event: 'custom_segment_query_error' });
    } finally {
      setIsQuerySubmitting(false);
    }
  };

  return (
    <Stack p={2} pb={9}>
      <Typography variant="h5">{isEdit ? 'Edit' : 'Create'} Custom Segment</Typography>
      <FormProvider {...form}>
        <Box mt={2} p={2} sx={{ background: 'white', borderRadius: '5px' }}>
          <Typography variant="h6">General</Typography>
          <Box mt={2}>
            <TextField label="Name" name="name" />
          </Box>
          <Box mt={1}>
            <Checkbox
              name="retireExempt"
              label="Do not auto-retire"
              normalizeOutput={(e) => e.target.checked}
            />
          </Box>
          <Stack mt={2} spacing={2} width={1}>
            <Typography>Files</Typography>
            {pathsFieldArray &&
              pathsFieldArray.map((id, index) => (
                <Box key={`paths.id.${id}`} mt={1}>
                  <Controller
                    name={`paths.${index}`}
                    control={control}
                    render={({ field }) => (
                      <Paper sx={{ width: '80%', padding: '16px 0' }}>
                        <Typography sx={{ margin: '0 16px' }}>{field.value}</Typography>
                      </Paper>
                    )}
                  />
                </Box>
              ))}
            <Stack direction="row" spacing={1} mt={1}>
              <TextField name="s3FilePath" label="S3 File Path" sx={{ width: '80%' }} placeholder="s3://path/to/file" />
              <Stack>
                <Button onClick={() => onAddS3FilePath()}>Add File Path</Button>
              </Stack>
            </Stack>
            <S3Upload
              // Give it a bogus field name "unusedFieldName" as we won't be doing something different
              // with onS3UploadSuccess
              name="unusedFieldName"
              accept=".csv,.tsv"
              s3InfoUrl={API_S3_UPLOAD_INFO_URL}
              generateKey={generateKey}
              onSuccess={onS3UploadSuccess}
              preserveName
              sx={{ width: 0.8 }}
            />
            <Stack direction="row">
              {!isEmpty(pathsFieldArray) && <Button onClick={onClearFiles}>Clear Files</Button>}
            </Stack>
          </Stack>
        </Box>
        <Box mt={2} p={2} sx={{ background: 'white', borderRadius: '5px' }}>
          <Typography variant="h6">Query</Typography>
          <Box mt={2}>
            <MuiTextField
              label="Segment via Query"
              variant="standard"
              size="small"
              multiline
              rows={6}
              fullWidth
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              placeholder='Enter Query Here'
              inputProps={{ style: { resize: 'vertical' } }}
            />
          </Box>
          {queryError && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography color="error">{queryError}</Typography>
            </Box>
          )}
          <Stack direction="row" mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleQuerySubmit}
              disabled={isQuerySubmitting || !queryText.trim()}
              startIcon={isQuerySubmitting ? <CircularProgress size={20} /> : null}
            >
              Submit Query
            </Button>
          </Stack>
        </Box>
        <Box mt={2}>
          {tasks && tasks.apiData && tasks.apiData.length > 0 && (
            <Box mt={2} py={2} p={1} sx={{ background: 'white', borderRadius: '5px' }}>
              <Typography variant="h6">Tasks List</Typography>
              <Table style={{ width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2}>Source</TableCell>
                    <TableCell colSpan={2}>Subtasks</TableCell>
                    <TableCell colSpan={2}>Imported Users</TableCell>
                    <TableCell rowSpan={2}>Start Date</TableCell>
                    <TableCell rowSpan={2}>End Date</TableCell>
                    <TableCell rowSpan={2}>Errors</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Count</TableCell>
                    <TableCell>% Completed</TableCell>
                    <TableCell>Success</TableCell>
                    <TableCell>Failed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.apiData.map((task) => (
                    <TaskErrorRow key={task.id} task={task} />
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
        {isEdit && customSegmentId && (
          <Box mt={2}>
            <AssociatedAdGroups segmentId={customSegmentId} />
          </Box>
        )}
        <FormEndOptions position="fixed">
          <Stack direction="row" alignItems="center" spacing={2}>
            {renderFooter(handleSubmit(onSubmit), isDirty, form)}
          </Stack>
        </FormEndOptions>
      </FormProvider>

    </Stack>
  );
}

export default CustomSegmentsForm;
